<template>
  <div class="w-full">
    <div class="flex bg-grey-1 px-4 py-2">
      <div class="flex-1 flex">
        <avatar
          size="12"
          class="mr-2 cursor-pointer"
          :user="reply.owner"
          @click="show.user = true"
        />
        <div class="flex flex-1 flex-col justify-between">
          <div>
            <a class="cursor-pointer" @click="show.user = true">
              <username class="font-bold" :user="reply.owner" />
            </a>
          </div>
          <div class="flex">
            <span
              class="font-semibold text-sm uppercase text-grey-5"
              :title="dateFormat(reply.created_at)"
            >{{ fromNow(reply.created_at) }}</span>
            <span
              v-if="reply.edited_at"
              class="mr-1 font-semibold text-sm uppercase text-grey-4 ml-1 md:hidden"
              :title="dateFormat(reply.edited_at)"
            >*</span>
            <span
              v-if="reply.edited_at"
              class="mr-1 font-semibold text-sm uppercase text-grey-4 ml-1 hidden md:inline-block"
              :title="dateFormat(reply.edited_at)"
            >(Edited {{ fromNow(reply.edited_at) }})</span>
          </div>
        </div>
        <div class="flex items-center text-lg text-blue-9">
          <router-link v-if="!editing" :to="link" class="cursor-pointer hover:text-blue-7">
            <link-icon></link-icon>
          </router-link>
          <a
            @click="editing = !editing"
            class="cursor-pointer ml-2 hover:text-blue-7"
            v-if="authCanModify || (authOwns && !thread.locked)"
          >
            <edit-icon v-if="!editing"></edit-icon>
            <x-square-icon v-else></x-square-icon>
          </a>
          <a
            @click="show.delete = true"
            class="cursor-pointer ml-2 hover:text-blue-7"
            v-if="(authCanModify || (authOwns && !thread.locked)) && !editing"
          >
            <trash2-icon></trash2-icon>
          </a>
          <a
            @click="replyWithQuote"
            class="cursor-pointer ml-2 hover:text-blue-7"
            v-if="$auth.check() && $auth.user().verified && !editing"
          >
            <svgicon class="w-4 h-4 fill-current" icon="quote"></svgicon>
          </a>
        </div>
      </div>
    </div>

    <div class="w-full px-4 py-2">
      <transition name="fade" mode="out-in">
        <div v-if="!editing" key="view">
          <rich-content-item v-if="!editing" :html="reply.body" class="pb-2"></rich-content-item>

          <div
            class="py-2 -mx-4 px-4 flex -mx-4 border-t border-blue-4"
            :class="{'pb-0': reply.edited_at}"
            v-if="reply.owner.signature"
          >
            <div class="w-full text-grey-9 | rich-content" v-html="reply.owner.signature"></div>
          </div>

          <div
            class="py-1 -mx-4 -mb-2 px-4 flex -mx-4 border-t border-blue-3 bg-grey-1"
            v-if="reply.edited_at"
          >
            <div
              class="w-full text-blue-7 text-sm"
            >Last edited {{ dateFormat(reply.edited_at) }} by {{Object.keys(reply.editor).length ? reply.editor.username : '[removed]' }}.</div>
          </div>
        </div>

        <reply-update-form
          class="py-2 | rich-content"
          v-else
          :existing="reply"
          @success="editSuccess"
          key="edit"
        />
      </transition>
    </div>

    <portal to="modal-portal">
      <user-item v-if="show.user" :user="reply.owner" @close="show.user = false" />
    </portal>

    <portal to="modal-portal">
      <prompt v-if="show.delete" @close="show.delete = false" @confirm="deleteReply">
        This will delete the reply.
        <br />Are you sure?
      </prompt>
    </portal>
  </div>
</template>

<script>
import ReplyUpdateForm from "@/components/Forms/ReplyUpdateForm"
import RichContentItem from "@/components/Items/RichContentItem"
import DateTime from "@/mixins/DateTime"
import UserItem from "@/components/Items/UserItem"
import { EventBus } from "@/events"
import { EditIcon, LinkIcon, Trash2Icon, XSquareIcon } from "vue-feather-icons"

export default {
  name: "ReplyItem",
  props: {
    thread: {
      required: true
    },
    reply: {
      required: true
    }
  },
  mixins: [DateTime],
  components: {
    ReplyUpdateForm,
    RichContentItem,
    UserItem,
    EditIcon,
    XSquareIcon,
    Trash2Icon,
    LinkIcon
  },
  data() {
    return {
      editing: false,
      show: {
        user: false,
        delete: false
      }
    }
  },
  computed: {
    authOwns() {
      return this.$auth.user().username === this.reply.owner.username
    },
    authCanModify() {
      if (this.$auth.check(["admin", "super-moderator"])) return true
      if (
        this.$auth.check(["moderator"]) &&
        this.$auth
          .user()
          .moderatable_channels.indexOf(this.$route.params.channel) !== -1
      )
        return true

      return false
    },
    link() {
      let to = {
        name: "threads.show",
        params: {
          channel: this.$route.params.channel,
          thread: this.$route.params.thread
        },
        hash: "#reply-" + this.reply.id,
        query: {}
      }

      if (this.$route.query.page) to.query.page = this.$route.query.page

      return to
    }
  },
  methods: {
    editSuccess() {
      this.$parent.fetch()
      this.editing = false
    },
    deleteReply() {
      this.show.delete = false
      this.$api.replies
        .destroy(this.reply.id)
        .then(() => {
          this.$toasted.success("Reply deleted.")
          this.$emit("deleted")
        })
        .catch(() => {
          this.$error.generic()
        })
    },
    replyWithQuote() {
      EventBus.$emit("reply-with-quote", this.reply)
    }
  }
}
</script>
